import Loading from '@/components/Loading/Loading';
import { GraphCanvas } from '@/components/UI/GraphCanvas/GraphCanvas';
import { createGraphCanvasContext } from '@/components/UI/GraphCanvas/GraphCanvasContext';
import { GraphCanvasEngine } from '@/components/UI/GraphCanvas/GraphCanvasEngine';
import ButtonNode from '@/components/UI/GraphCanvas/nodes/ButtonNode';
import ReadOnlyNode from '@/components/UI/GraphCanvas/nodes/ReadOnlyNode';
import type { AgentSessionApiId } from '@/model/Agent.model';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import type { NodeTypes } from '@xyflow/react';
import { type FC, useEffect, useState } from 'react';
import type { AgentSessionMeta, AgentTreeNodeData, ViewReportHandler } from './AgentTree.utils';
import styles from './AgentSessionTree.module.scss';
import { agentTreeGraphConfig, mapTreeToSnapshot } from './AgentTree.utils';

/**
 * Node component registry, keyed on node variant (`read_only` | `report`) and mapped to the generic
 * GraphCanvas node components. The agent-specific content is stamped onto each node's `data` during
 * mapping, so the registry wires variants to shells only. Module scope — never inline.
 */
const nodeTypes: NodeTypes = {
  read_only: ReadOnlyNode,
  report: ButtonNode,
};

/** The agent tree's fully-typed GraphCanvas provider + hooks (one instantiation for this feature). */
const { GraphCanvasProvider, useGraphCanvasNodes, useGraphCanvasEdges } = createGraphCanvasContext<
  AgentTreeNodeData,
  AgentSessionMeta
>();

/** Reads the engine via hooks and renders the read-only canvas. Must sit under `GraphCanvasProvider`. */
const AgentSessionTreeCanvas: FC = () => {
  const nodes = useGraphCanvasNodes();
  const edges = useGraphCanvasEdges();
  return <GraphCanvas<AgentTreeNodeData> nodes={nodes} edges={edges} nodeTypes={nodeTypes} />;
};

interface AgentSessionTreeProps {
  /** The session whose tree to render; drives the `GET /tree` fetch. */
  sessionApiId: AgentSessionApiId;
  /**
   * Invoked with a result node's api id when its "View Report" action is triggered. Supplied by the
   * page so this component stays a pure snapshot renderer with no modal/navigation coupling.
   */
  onViewReport: ViewReportHandler;
}

/**
 * Renders the tree of an agent session as a read-only React Flow graph. Fetches the session tree from
 * `GET /ai-agents/sessions/{sessionApiId}/tree` and hydrates the graph engine once from the response —
 * a snapshot render, no live streaming yet. Shows a spinner while loading and an inline message on
 * failure. A result node's "View Report" action calls `onViewReport` (the page opens the report modal).
 */
const AgentSessionTree: FC<AgentSessionTreeProps> = ({ sessionApiId, onViewReport }) => {
  const [engine] = useState(() => new GraphCanvasEngine<AgentTreeNodeData, AgentSessionMeta>(agentTreeGraphConfig));

  const { response, loading } = useApiHook({
    endpoint: 'GET /api/v1/ai-agents/sessions/{sessionApiId}/tree',
    path: { sessionApiId },
    options: {
      onError: {
        // Return the typed error response rather than throwing, and skip the default toast — the tree
        // renders its own inline error state. Still capture to Sentry for observability.
        throw: false,
        preserveOriginalError: false,
        toast: false,
        capture: true,
        slug: 'agent-session-tree-load',
      },
    },
  });

  // Hydrate the engine from a successful /tree response. The error case is handled by the early
  // return below (the canvas never mounts), so there is nothing to set on the engine here.
  useEffect(() => {
    if (response?.type === 'success') {
      engine.setGraph(mapTreeToSnapshot(response.body, onViewReport));
    }
  }, [response, engine, onViewReport]);

  if (loading) {
    return (
      <div className={styles.container} data-test-component="AgentSessionTree">
        <Loading txt="Loading session tree…" />
      </div>
    );
  }

  if (response?.type === 'error') {
    return (
      <div className={styles.container} data-test-component="AgentSessionTree">
        <p className={styles.status} data-test-element="error">
          Couldn't load the session tree.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.container} data-test-component="AgentSessionTree">
      <GraphCanvasProvider store={engine}>
        <AgentSessionTreeCanvas />
      </GraphCanvasProvider>
    </div>
  );
};

export default AgentSessionTree;
